import Swiper from 'swiper/bundle';
import 'swiper/swiper-bundle.css';

const TITLE_PRE_SETS = 2;
const TITLE_TOTAL_SETS = 5;
const CONTENT_PRE_SETS = 1;
const CONTENT_TOTAL_SETS = 3;
const AUTOPLAY_DELAY = 2500;

document.addEventListener( 'DOMContentLoaded', () => {
  const style = document.createElement( 'style' );
  style.textContent =
    '.swiper-no-transition .swiper-slide,' +
    '.swiper-no-transition .swiper-slide *' +
    '{ transition: none !important; }';
  document.head.appendChild( style );

  const wrappers = document.querySelectorAll( '.history-slider-wrapper' );
  if ( !wrappers.length ) {
    return;
  }
  wrappers.forEach( initHistorySlider );
} );

function initHistorySlider( wrapper ) {
  const titlesEl = wrapper.querySelector( '.history-slider-titles' );
  const contentsEl = wrapper.querySelector( '.history-slider-contents' );
  if ( !titlesEl || !contentsEl ) {
    return;
  }

  const ctx = createSliderContext( wrapper, titlesEl, contentsEl );
  attachTransitionHandler( ctx );
  attachNavButtons( ctx );
  attachTitleClickHandler( ctx );
  attachPaginationClickHandler( ctx );
  attachAutoplay( ctx, wrapper );
}

function createSwiperInstance( el, initialSlide, isContent ) {
  const config = JSON.parse( el.getAttribute( 'data-swiper' ) ) || {};
  config.loop = false;
  config.autoplay = false;
  config.initialSlide = initialSlide;
  delete config.navigation;

  if ( isContent ) {
    delete config.thumbs;
    delete config.pagination;
  }

  return new Swiper( el, config );
}

function createSliderContext( wrapper, titlesEl, contentsEl ) {
  const middleIndex =
    parseInt( wrapper.getAttribute( 'data-middle-index' ), 10 ) || 0;

  const slideCount = duplicateSlides(
    titlesEl, TITLE_PRE_SETS, TITLE_TOTAL_SETS
  );
  duplicateSlides( contentsEl, CONTENT_PRE_SETS, CONTENT_TOTAL_SETS );

  const titleCenter = TITLE_PRE_SETS * slideCount;
  const contentCenter = CONTENT_PRE_SETS * slideCount;

  const titlesSwiper = createSwiperInstance(
    titlesEl, titleCenter + middleIndex, false
  );
  const contentsSwiper = createSwiperInstance(
    contentsEl, contentCenter + middleIndex, true
  );

  const paginationEl = contentsEl.querySelector( '.swiper-pagination' );
  buildPagination( paginationEl, slideCount, middleIndex );
  updateTitleActive( titlesEl, middleIndex );

  return {
    titlesEl,
    contentsEl,
    titlesSwiper,
    contentsSwiper,
    paginationEl,
    slideCount,
    titleCenter,
    contentCenter,
    titleTotal: slideCount * TITLE_TOTAL_SETS,
    contentTotal: slideCount * CONTENT_TOTAL_SETS,
    lastSyncedReal: middleIndex,
    recentering: false,
    autoplayTimer: null,
  };
}

function silentRecenter( ctx, swiper, center, count, total ) {
  const idx = swiper.activeIndex;
  if ( idx < count || idx >= total - count ) {
    ctx.recentering = true;
    const real = getRealIndex( swiper );
    swiper.el.classList.add( 'swiper-no-transition' );
    swiper.slideTo( center + real, 0 );
    swiper.wrapperEl.getBoundingClientRect();
    swiper.el.classList.remove( 'swiper-no-transition' );
    ctx.recentering = false;
  }
}

function navigate( ctx, delta ) {
  silentRecenter(
    ctx, ctx.contentsSwiper,
    ctx.contentCenter, ctx.slideCount, ctx.contentTotal
  );
  silentRecenter(
    ctx, ctx.titlesSwiper,
    ctx.titleCenter, ctx.slideCount, ctx.titleTotal
  );

  ctx.contentsSwiper.slideTo(
    ctx.contentsSwiper.activeIndex + delta, 300
  );
  ctx.titlesSwiper.slideTo(
    ctx.titlesSwiper.activeIndex + delta, 300
  );

  const newReal = getRealIndex( ctx.contentsSwiper );
  ctx.lastSyncedReal = newReal;
  updateTitleActive( ctx.titlesEl, newReal );
  updatePagination( ctx.paginationEl, newReal );
}

function navigateToReal( ctx, targetReal ) {
  const currentReal = getRealIndex( ctx.contentsSwiper );
  if ( targetReal === currentReal ) {
    return;
  }

  const forward =
    ( targetReal - currentReal + ctx.slideCount ) % ctx.slideCount;
  const backward =
    ( currentReal - targetReal + ctx.slideCount ) % ctx.slideCount;
  const delta = forward <= backward ? forward : -backward;

  ctx.recentering = true;
  ctx.contentsSwiper.el.classList.add( 'swiper-no-transition' );
  ctx.titlesSwiper.el.classList.add( 'swiper-no-transition' );
  ctx.contentsSwiper.slideTo( ctx.contentCenter + currentReal, 0 );
  ctx.titlesSwiper.slideTo( ctx.titleCenter + currentReal, 0 );
  ctx.contentsSwiper.wrapperEl.getBoundingClientRect();
  ctx.titlesSwiper.wrapperEl.getBoundingClientRect();
  ctx.contentsSwiper.el.classList.remove( 'swiper-no-transition' );
  ctx.titlesSwiper.el.classList.remove( 'swiper-no-transition' );
  ctx.recentering = false;

  ctx.contentsSwiper.slideTo(
    ctx.contentsSwiper.activeIndex + delta, 300
  );
  ctx.titlesSwiper.slideTo(
    ctx.titlesSwiper.activeIndex + delta, 300
  );

  ctx.lastSyncedReal = targetReal;
  updateTitleActive( ctx.titlesEl, targetReal );
  updatePagination( ctx.paginationEl, targetReal );
}

function stopAutoplay( ctx ) {
  if ( ctx.autoplayTimer ) {
    clearInterval( ctx.autoplayTimer );
    ctx.autoplayTimer = null;
  }
}

function attachTransitionHandler( ctx ) {
  ctx.contentsSwiper.on( 'slideChangeTransitionEnd', () => {
    if ( ctx.recentering ) {
      return;
    }
    const newReal = getRealIndex( ctx.contentsSwiper );
    if ( newReal !== ctx.lastSyncedReal ) {
      ctx.lastSyncedReal = newReal;
      ctx.recentering = true;
      ctx.titlesSwiper.el.classList.add( 'swiper-no-transition' );
      updateTitleActive( ctx.titlesEl, newReal );
      ctx.titlesSwiper.slideTo( ctx.titleCenter + newReal, 0 );
      ctx.titlesSwiper.wrapperEl.getBoundingClientRect();
      ctx.titlesSwiper.el.classList.remove( 'swiper-no-transition' );
      ctx.recentering = false;
      updatePagination( ctx.paginationEl, newReal );
    }
    silentRecenter(
      ctx, ctx.contentsSwiper,
      ctx.contentCenter, ctx.slideCount, ctx.contentTotal
    );
    silentRecenter(
      ctx, ctx.titlesSwiper,
      ctx.titleCenter, ctx.slideCount, ctx.titleTotal
    );
  } );
}

function attachNavButtons( ctx ) {
  const prevBtn = ctx.contentsEl.querySelector( '.swiper-button-prev' );
  const nextBtn = ctx.contentsEl.querySelector( '.swiper-button-next' );

  if ( prevBtn ) {
    prevBtn.addEventListener( 'click', ( e ) => {
      e.preventDefault();
      e.stopPropagation();
      navigate( ctx, -1 );
      stopAutoplay( ctx );
    } );
  }
  if ( nextBtn ) {
    nextBtn.addEventListener( 'click', ( e ) => {
      e.preventDefault();
      e.stopPropagation();
      navigate( ctx, 1 );
      stopAutoplay( ctx );
    } );
  }
}

function parseRealIndex( el, selector ) {
  const target = el.closest( selector );
  if ( !target ) {
    return NaN;
  }
  return parseInt( target.getAttribute( 'data-real-index' ), 10 );
}

function attachTitleClickHandler( ctx ) {
  ctx.titlesEl.addEventListener( 'click', ( e ) => {
    const realIndex = parseRealIndex( e.target, '.swiper-slide' );
    if ( isNaN( realIndex ) ) {
      return;
    }
    navigateToReal( ctx, realIndex );
    stopAutoplay( ctx );
  } );
}

function attachPaginationClickHandler( ctx ) {
  if ( !ctx.paginationEl ) {
    return;
  }
  ctx.paginationEl.addEventListener( 'click', ( e ) => {
    const realIndex = parseRealIndex(
      e.target, '.swiper-pagination-bullet'
    );
    if ( isNaN( realIndex ) ) {
      return;
    }
    navigateToReal( ctx, realIndex );
    stopAutoplay( ctx );
  } );
}

function attachAutoplay( ctx, wrapper ) {
  ctx.contentsSwiper.on( 'touchStart', () => stopAutoplay( ctx ) );

  const observer = new IntersectionObserver(
    ( entries ) => {
      if ( entries[ 0 ].isIntersecting ) {
        stopAutoplay( ctx );
        ctx.autoplayTimer = setInterval(
          () => navigate( ctx, 1 ), AUTOPLAY_DELAY
        );
        observer.disconnect();
      }
    },
    { threshold: 0.2 }
  );
  observer.observe( wrapper );
}

function duplicateSlides( el, preSets, totalSets ) {
  const swiperWrapper = el.querySelector( '.swiper-wrapper' );
  const slides = [ ...swiperWrapper.children ];
  const count = slides.length;

  slides.forEach( ( slide, i ) =>
    slide.setAttribute( 'data-real-index', i )
  );

  for ( let set = 0; set < preSets; set++ ) {
    const frag = document.createDocumentFragment();
    slides.forEach( ( slide, i ) => {
      const clone = slide.cloneNode( true );
      clone.setAttribute( 'data-real-index', i );
      frag.appendChild( clone );
    } );
    swiperWrapper.insertBefore( frag, swiperWrapper.firstChild );
  }

  const appendSets = totalSets - preSets - 1;
  for ( let set = 0; set < appendSets; set++ ) {
    slides.forEach( ( slide, i ) => {
      const clone = slide.cloneNode( true );
      clone.setAttribute( 'data-real-index', i );
      swiperWrapper.appendChild( clone );
    } );
  }

  return count;
}

function getRealIndex( swiper ) {
  const slide = swiper.slides[ swiper.activeIndex ];
  return parseInt( slide?.getAttribute( 'data-real-index' ), 10 ) || 0;
}

function updateTitleActive( titlesEl, realIndex ) {
  titlesEl.querySelectorAll( '.swiper-slide' ).forEach( ( slide ) => {
    const match =
      parseInt( slide.getAttribute( 'data-real-index' ), 10 ) === realIndex;
    slide.classList.toggle( 'swiper-slide-thumb-active', match );
  } );
}

function buildPagination( el, count, activeIndex ) {
  if ( !el ) {
    return;
  }
  el.innerHTML = '';
  for ( let i = 0; i < count; i++ ) {
    const bullet = document.createElement( 'span' );
    bullet.className = 'swiper-pagination-bullet';
    if ( i === activeIndex ) {
      bullet.classList.add( 'swiper-pagination-bullet-active' );
    }
    bullet.setAttribute( 'data-real-index', i );
    el.appendChild( bullet );
  }
}

function updatePagination( el, realIndex ) {
  if ( !el ) {
    return;
  }
  el.querySelectorAll( '.swiper-pagination-bullet' ).forEach( ( bullet ) => {
    const match =
      parseInt( bullet.getAttribute( 'data-real-index' ), 10 ) === realIndex;
    bullet.classList.toggle( 'swiper-pagination-bullet-active', match );
  } );
}
